import { Button, Drawer, Empty, Tooltip } from 'antd';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import styles from '@/styles/components/wallet-menu.module.less';
import AddressIcon from '@/components/AddressIcon';
import { useCombineSelector, useTranslation, useWallet } from '@/services';
import Image from '@/components/LocalImage';
import { iteratorTo, sensitiveAddress } from '@/utils';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, LoadingOutlined, LogoutOutlined } from '@ant-design/icons';
import TokenBalanceFmt from '@/components/token/TokenBalanceFmt';
import ConstractNames from '@/services/contract.constants';
import { TransactionModel, REDUCER_NAME } from '@/services/reducers/transactions';
import { SupportChains } from '@/config';
import { disconnect } from '@reducers/wallet';
import { ContractTransaction } from 'ethers';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGetContractAddress } from '@/services/contracts';

const Transactions = ({ visible, onClose }: { visible?: boolean; onClose?: () => void }): ReactElement => {
    return (
        <Drawer open={visible} title={<TransactionWalletInfo />} className={styles['transactions']} onClose={onClose} closeIcon={null}>
            <TransactionList />
        </Drawer>
    );
};

const TransactionWalletInfo = (): ReactElement => {
    const { address } = useWallet();
    const dispatch = useDispatch();
    return (
        <div className={styles['wallet-info-box']}>
            <Link to="/admin/mint">
                <>
                    <span className={styles['icon']}>
                        <AddressIcon address={address} />
                    </span>
                    <span className={styles['address']}>{sensitiveAddress(address)}</span>
                </>
            </Link>
            <Button type="link" onClick={() => dispatch(disconnect())} className={styles['logout-icon']}>
                <LogoutOutlined style={{ fontSize: 24, color: '#999', verticalAlign: 'middle' }} />
            </Button>
            <BalanceAmount />
        </div>
    );
};

const BalanceAmount = (): ReactElement => {
    return (
        <div className={styles['balances-box']}>
            <TokenBalanceLink token={ConstractNames.Names.AGDToken} icon={<Image name="token-icon" type="svg" dir="/images/personal" />} />
            <TokenBalanceLink token={ConstractNames.Names.AGSToken} icon={<Image name="ctoken" type="svg" dir="/images/token" />} />
        </div>
    );
};

const TokenBalanceLink = ({ token, icon }: { token: string; icon: ReactElement }): ReactElement => {
    const { address, chain } = useWallet();
    const tokenAddress = useGetContractAddress(token);
    const href = useMemo(() => {
        if (!address || !chain?.explorer || !tokenAddress) return undefined;
        return `${chain.explorer}/token/${tokenAddress}?a=${address}`;
    }, [address, chain?.explorer, tokenAddress]);
    return (
        <Button icon={icon} href={href} target="_blank" rel="noreferrer" disabled={!href}>
            <TokenBalanceFmt token={token} />
        </Button>
    );
};

const TransactionList = (): ReactElement => {
    const transactions = useCombineSelector<TransactionModel>(REDUCER_NAME, (state) => state.transactions) as TransactionResult[];
    const { t } = useTranslation('common', 'transactions');
    if (transactions.length == 0) {
        return <Empty description={<span>{t('no-data')}</span>} className={styles['no-data']} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }
    return iteratorTo(
        transactions,
        (li) => <ul>{li}</ul>,
        (t, i) => <TransactionItem key={t.id} {...t} />
    );
};

const TransactionItem = (props: TransactionResult): ReactElement => {
    const [state, setState] = useState(<LoadingOutlined />);
    const { chainName } = useWallet();
    const { t } = useTranslation('common', 'transactions');
    const chainData = useMemo(() => _.findLast(SupportChains, (o) => o.name == chainName), [chainName]);
    const hasTxHash = !!props.transaction?.hash;
    const toExplorerLink = useMemo(() => {
        if (!chainData || !props.transaction) return undefined;
        return chainData.txLink((props.transaction as ContractTransaction).hash);
    }, [props.state, chainData]);
    const errorMessage = useMemo(() => {
        if (!props.error) return '';
        if (typeof props.error === 'string') return props.error;
        if (props.error.fallbackMessage) return String(props.error.fallbackMessage);
        if (props.error.messageKey) return t(props.error.messageKey);
        return t('error.unknown');
    }, [props.error, t]);
    const tooltipTitle = props.state === 'Failed' || props.state === 'Fail' ? errorMessage || props.data : props.data;
    useEffect(() => {
        switch (props.state) {
            case 'Failed':
            case 'Fail':
                setState(<CloseCircleOutlined style={{ color: 'red' }} />);
                break;
            case 'Confirmed':
            case 'Success':
                setState(<CheckCircleOutlined />);
                break;
            case 'Submitted':
            case 'Wait':
                setState(<LoadingOutlined />);
                break;
            case 'Preparing':
            case 'Init':
                setState(<ClockCircleOutlined />);
                break;
        }
    }, [props.state]);
    const content = (
        <>
            <Tooltip title={tooltipTitle}>
                <span className={styles['desc']}>{props.data}</span>
            </Tooltip>
            <span className={styles['loading']}>{state}</span>
        </>
    );
    return (
        <li>
            {hasTxHash && toExplorerLink ? (
                <a href={toExplorerLink} target="_blank" rel="noreferrer">
                    {content}
                </a>
            ) : (
                content
            )}
        </li>
    );
};

export default Transactions;
